import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaExclamationCircle, FaTimes } from 'react-icons/fa';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Animation variants for the form
const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' },
    },
};

const inputVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, delay: i * 0.1, ease: 'easeOut' },
    }),
};

const hoverVariants = {
    hover: {
        scale: 1.05,
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
        transition: { duration: 0.3, ease: 'easeOut' },
    },
};

// Animation variants for the toast notification
const toastVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
    exit: {
        opacity: 0,
        y: -50,
        transition: { duration: 0.3, ease: 'easeIn' },
    },
};

const Waitlist = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setLoading(true);

    // Client-side validation
    if (!formData.name || !formData.email) {
      setStatus({ type: 'error', message: 'Name and email are required' });
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatus({ type: 'error', message: 'Invalid email format' });
      setLoading(false);
      setTimeout(() => setStatus(null), 5000);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', message: 'Thank you for joining the waitlist! We’ll be in touch soon.' });
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus(null), 5000);
      } else {
        setStatus({ type: 'error', message: result.error || 'Failed to join the waitlist. Please try again.' });
        setTimeout(() => setStatus(null), 5000);
      }
    } catch (error) {
      setStatus({ type: 'error', message: 'An error occurred. Please try again later.' });
      setTimeout(() => setStatus(null), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setStatus(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 flex flex-col font-sans mt-10">
      <style>
        {`
          .glow-button {
            animation: glow 1.5s ease-in-out infinite alternate;
          }
          @keyframes glow {
            from {
              box-shadow: 0 0 5px rgba(37, 150, 190, 0.4), 0 0 10px rgba(37, 150, 190, 0.3);
            }
            to {
              box-shadow: 0 0 15px rgba(37, 150, 190, 0.8), 0 0 25px rgba(37, 150, 190, 0.5);
            }
          }
          .toast-gradient-success {
            background: linear-gradient(90deg, #2596be 0%, #1e7a9b 100%);
          }
          .toast-gradient-error {
            background: linear-gradient(90deg, #f87171 0%, #ef4444 100%);
          }
        `}
      </style>

      <Header />

      {status && (
        <motion.div
          className={`fixed top-20 left-1/2 transform -translate-x-1/2 max-w-md w-full mx-auto flex items-center px-4 py-3 rounded-xl shadow-2xl z-50 border border-opacity-20 backdrop-blur-sm ${
            status.type === 'success'
              ? 'toast-gradient-success border-[#2596be] text-white'
              : 'toast-gradient-error border-red-500 text-white'
          }`}
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={toastVariants}
        >
          <div className="flex items-center flex-1">
            {status.type === 'success' ? (
              <FaCheckCircle className="text-xl mr-3" />
            ) : (
              <FaExclamationCircle className="text-xl mr-3" />
            )}
            <p className="text-sm font-medium">{status.message}</p>
          </div>
          <button
            onClick={handleCloseToast}
            className="ml-4 text-white hover:text-gray-200 focus:outline-none"
            aria-label="Close notification"
          >
            <FaTimes className="text-lg" />
          </button>
        </motion.div>
      )}

      <motion.div
        variants={formVariants}
        initial="hidden"
        animate="visible"
        className="flex-grow flex items-center justify-center py-12"
      >
        <div className="max-w-2xl w-full p-8 bg-white rounded-lg shadow-md border border-gray-200">
          <h2 className="text-3xl font-bold text-center text-[#2596be] mb-4">
            Join the A.I Hire Waitlist
          </h2>
          <p className="text-gray-600 text-center mb-8">
            Be the first to access A.I Hire when it launches.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div variants={inputVariants} custom={0}>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2596be]"
              />
            </motion.div>

            <motion.div variants={inputVariants} custom={1}>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2596be]"
              />
            </motion.div>

            <motion.div variants={inputVariants} custom={2}>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                id="message"
                name="message"
                rows="4"
                placeholder="Why are you interested in A.I Hire?"
                value={formData.message}
                onChange={handleChange}
                disabled={loading}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#2596be]"
              />
            </motion.div>

            <motion.div variants={inputVariants} custom={3}>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover="hover"
                variants={hoverVariants}
                className={`w-full px-6 py-3 bg-[#2596be] text-white rounded-lg font-medium glow-button ${
                  loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#1e7a9b]'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin h-4 w-4 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </span>
                ) : (
                  'Join the Waitlist'
                )}
              </motion.button>
            </motion.div>
          </form>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default Waitlist;